/* Watercolor Atlas: collection filtering and sorting (A10).
   Progressive enhancement over the native GET filter form:
   - submit on change (debounced while typing in the price fields)
   - fetch + swap the grid, count, chips, rail, and pagination via the
     Section Rendering API (no full page reload), with history support
   - mobile/tablet: the filter rail <details> becomes a slide-in drawer
     and the sort control moves inside it
   Without JS the <details> rail and the noscript Apply buttons keep working. */
(function () {
  'use strict';

  if (window.__dwFacetsLoaded) return;
  window.__dwFacetsLoaded = true;

  var DESKTOP = window.matchMedia('(min-width: 1025px)');
  var SWAP_TARGETS = [
    'data-dw-facets-count',
    'data-dw-facets-active',
    'data-dw-facets-grid',
    'data-dw-facets-pagination',
    'data-dw-facets-groups'
  ];

  function cssEscape(value) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function DwFacets(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.baseUrl = root.getAttribute('data-base-url') || window.location.pathname;
    this.form = document.getElementById('DwFacetsForm-' + this.sectionId);
    this.drawer = root.querySelector('[data-dw-facets-drawer]');
    this.sortWrap = root.querySelector('[data-dw-sort-wrap]');
    this.sortHome = this.sortWrap ? this.sortWrap.parentNode : null;
    this.sortSlot = root.querySelector('[data-dw-sort-slot]');
    this.typeTimer = null;
    this.controller = null;
    /* Per-group UI state (Show More expansion, search query), keyed by the
       group's param name so it survives Section Rendering API re-renders. */
    this.groupState = {};

    this.onChange = this.onChange.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onRootKeydown = this.onRootKeydown.bind(this);
    this.onPopstate = this.onPopstate.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);

    root.addEventListener('change', this.onChange);
    root.addEventListener('input', this.onInput);
    root.addEventListener('click', this.onClick);
    root.addEventListener('keydown', this.onRootKeydown);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('popstate', this.onPopstate);

    if (this.drawer) {
      this.drawer.classList.add('dw-facets--js');
      this.drawer.addEventListener('toggle', this.onToggle);
    }

    if (DESKTOP.addEventListener) {
      DESKTOP.addEventListener('change', this.onBreakpoint);
    } else if (DESKTOP.addListener) {
      DESKTOP.addListener(this.onBreakpoint);
    }
    this.onBreakpoint();
    this.enhanceGroups();
  }

  DwFacets.prototype = {
    ownsControl: function (el) {
      return !!(el && this.form && el.form === this.form);
    },

    isFacetSearch: function (el) {
      return !!(el && el.hasAttribute && el.hasAttribute('data-dw-facet-search'));
    },

    /* ------------------------------------------------------ form events */
    onChange: function (event) {
      var target = event.target;
      if (this.isFacetSearch(target)) return;
      if (!this.ownsControl(target)) return;
      window.clearTimeout(this.typeTimer);
      this.submit();
    },

    onInput: function (event) {
      var target = event.target;
      if (this.isFacetSearch(target)) {
        this.onSearchInput(target);
        return;
      }
      if (!this.ownsControl(target) || target.type !== 'number') return;
      var self = this;
      window.clearTimeout(this.typeTimer);
      this.typeTimer = window.setTimeout(function () {
        self.submit();
      }, 600);
    },

    onRootKeydown: function (event) {
      /* The value-search box filters the list live; Enter must not fire a
         native GET submit of the whole facet form. */
      if (event.key === 'Enter' && this.isFacetSearch(event.target)) {
        event.preventDefault();
      }
    },

    submit: function () {
      this.fetchAndRender(this.serialize(), true);
    },

    serialize: function () {
      var params = new URLSearchParams();
      if (!this.form) return '';
      var data = new FormData(this.form);
      data.forEach(function (value, key) {
        if (String(value).trim() !== '') params.append(key, value);
      });
      return params.toString();
    },

    /* ---------------------------------------------------- link handling */
    onClick: function (event) {
      var more = event.target.closest('[data-dw-more]');
      if (more && this.root.contains(more)) {
        this.toggleMore(more);
        return;
      }

      var closer = event.target.closest('[data-dw-facets-close]');
      if (closer && this.drawer && this.root.contains(closer)) {
        event.preventDefault();
        this.drawer.open = false;
        return;
      }

      var link = event.target.closest('[data-dw-facets-link], [data-dw-facets-pagination] a[href]');
      if (!link || !this.root.contains(link)) return;
      event.preventDefault();

      var url = new URL(link.href, window.location.origin);
      var query = url.searchParams.toString();
      this.fetchAndRender(query, true);
      this.syncSort(url.searchParams);
      if (link.closest('[data-dw-facets-pagination]')) this.scrollToTop();
    },

    onKeydown: function (event) {
      if (event.key !== 'Escape') return;
      if (!this.drawer || !this.drawer.open || DESKTOP.matches) return;
      if (!document.body.contains(this.root)) return;
      this.drawer.open = false;
      var toggle = this.drawer.querySelector('summary');
      if (toggle) toggle.focus();
    },

    onPopstate: function () {
      if (!document.body.contains(this.root)) return;
      var params = new URLSearchParams(window.location.search);
      this.fetchAndRender(params.toString(), false);
      this.syncSort(params);
    },

    /* ------------------------------------------------------ fetch + swap */
    fetchAndRender: function (query, pushHistory) {
      var self = this;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      var fetchUrl = this.baseUrl +
        '?section_id=' + encodeURIComponent(this.sectionId) +
        (query ? '&' + query : '');
      var displayUrl = this.baseUrl + (query ? '?' + query : '');

      this.setBusy(true);
      fetch(fetchUrl, { signal: this.controller.signal })
        .then(function (response) {
          if (!response.ok) throw new Error('Section render failed: ' + response.status);
          return response.text();
        })
        .then(function (html) {
          self.renderFrom(html);
          if (pushHistory) {
            window.history.pushState({ dwFacets: self.sectionId }, '', displayUrl);
          }
          self.setBusy(false);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          /* Graceful degrade: fall back to a full navigation. */
          window.location.href = displayUrl;
        });
    },

    renderFrom: function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');

      /* Remember which rail groups are open and where focus is. */
      var openGroups = {};
      this.root.querySelectorAll('[data-dw-group]').forEach(function (group) {
        openGroups[group.getAttribute('data-dw-group')] = group.open;
      });
      var active = document.activeElement;
      var focusKey = null;
      if (this.ownsControl(active) && active.name) {
        focusKey = { name: active.name, value: active.value, type: active.type };
      }
      var focusSearchGroup = null;
      if (this.isFacetSearch(active) && this.root.contains(active)) {
        var activeGroup = active.closest('[data-dw-group]');
        if (activeGroup) focusSearchGroup = activeGroup.getAttribute('data-dw-group');
      }

      var root = this.root;
      SWAP_TARGETS.forEach(function (attr) {
        var current = root.querySelector('[' + attr + ']');
        var next = doc.querySelector('[' + attr + ']');
        if (current && next) current.innerHTML = next.innerHTML;
      });

      this.root.querySelectorAll('[data-dw-group]').forEach(function (group) {
        var key = group.getAttribute('data-dw-group');
        if (key in openGroups) group.open = openGroups[key];
      });

      this.enhanceGroups();

      if (focusSearchGroup) {
        var searchInput = this.root.querySelector(
          '[data-dw-group="' + cssEscape(focusSearchGroup) + '"] [data-dw-facet-search]'
        );
        if (searchInput) searchInput.focus({ preventScroll: true });
      }

      if (focusKey) {
        var selector = '[name="' + cssEscape(focusKey.name) + '"]';
        var restored = null;
        this.root.querySelectorAll(selector).forEach(function (candidate) {
          if (restored) return;
          if (focusKey.type === 'checkbox' && candidate.value !== focusKey.value) return;
          restored = candidate;
        });
        if (restored) restored.focus({ preventScroll: true });
      }
    },

    setBusy: function (busy) {
      var value = busy ? 'true' : 'false';
      this.root.querySelectorAll(
        '[data-dw-facets-grid], [data-dw-facets-groups], [data-dw-facets-active], [data-dw-facets-pagination]'
      ).forEach(function (region) {
        region.setAttribute('aria-busy', value);
      });
    },

    syncSort: function (params) {
      if (!this.sortWrap) return;
      var select = this.sortWrap.querySelector('select[data-dw-sort]');
      if (!select) return;
      var value = params.get('sort_by');
      if (value) select.value = value;
    },

    scrollToTop: function () {
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    /* ------------------------------------------ long-list group controls
       Liquid renders every value (no-JS users get the full scrollable list)
       plus a hidden Show More button and a hidden search box on big groups.
       JS unhides them, tucks values past the first ten behind Show More,
       and filters rows live as the visitor types. State lives in
       this.groupState so re-renders keep expansion and queries. */
    groupStateFor: function (key) {
      if (!this.groupState[key]) {
        this.groupState[key] = { expanded: false, query: '' };
      }
      return this.groupState[key];
    },

    enhanceGroups: function () {
      var self = this;
      this.root.querySelectorAll('[data-dw-group]').forEach(function (group) {
        var state = self.groupStateFor(group.getAttribute('data-dw-group'));
        var searchWrap = group.querySelector('[data-dw-search-wrap]');
        if (searchWrap) searchWrap.hidden = false;
        var search = group.querySelector('[data-dw-facet-search]');
        if (search && search.value !== state.query) search.value = state.query;
        if (group.querySelector('[data-dw-more]')) {
          self.applyExpanded(group, state.expanded);
        }
        self.applySearch(group, state.query);
      });
    },

    toggleMore: function (button) {
      var group = button.closest('[data-dw-group]');
      if (!group) return;
      var state = this.groupStateFor(group.getAttribute('data-dw-group'));
      state.expanded = !state.expanded;
      this.applyExpanded(group, state.expanded);
    },

    applyExpanded: function (group, expanded) {
      group.classList.toggle('dw-facets__group--capped', !expanded);
      var more = group.querySelector('[data-dw-more]');
      if (!more) return;
      more.hidden = false;
      more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      more.textContent = expanded
        ? 'Show Less'
        : 'Show More (' + (more.getAttribute('data-dw-more-count') || '') + ')';
    },

    onSearchInput: function (input) {
      var group = input.closest('[data-dw-group]');
      if (!group) return;
      var state = this.groupStateFor(group.getAttribute('data-dw-group'));
      state.query = input.value;
      this.applySearch(group, state.query);
    },

    applySearch: function (group, query) {
      var q = (query || '').trim().toLowerCase();
      var searching = q !== '';
      group.classList.toggle('dw-facets__group--searching', searching);
      var matches = 0;
      group.querySelectorAll('.dw-facets__option').forEach(function (option) {
        var name = option.querySelector('.dw-facets__name');
        var hit = !searching || (name && name.textContent.toLowerCase().indexOf(q) !== -1);
        option.classList.toggle('dw-facets__option--miss', !hit);
        if (hit) matches += 1;
      });
      var noMatch = group.querySelector('[data-dw-no-match]');
      if (noMatch) noMatch.hidden = !searching || matches > 0;
      /* While searching, matches from the whole list show, so the Show More
         toggle would only confuse; it returns when the query clears. */
      var more = group.querySelector('[data-dw-more]');
      if (more) more.hidden = searching;
    },

    /* --------------------------------------------------- drawer behavior */
    onToggle: function () {
      var smallScreen = !DESKTOP.matches;
      if (this.drawer.open && smallScreen) {
        document.body.style.overflow = 'hidden';
        var first = this.drawer.querySelector(
          '.dw-facets__panel select, .dw-facets__panel input:not([disabled]), .dw-facets__panel button'
        );
        if (first) first.focus({ preventScroll: true });
      } else {
        document.body.style.overflow = '';
      }
    },

    onBreakpoint: function () {
      if (DESKTOP.matches) {
        /* Static rail: force open, undo drawer chrome, park sort in toolbar. */
        if (this.drawer) this.drawer.open = true;
        document.body.style.overflow = '';
        if (this.sortWrap && this.sortHome && this.sortWrap.parentNode !== this.sortHome) {
          this.sortHome.appendChild(this.sortWrap);
        }
        if (this.sortSlot) this.sortSlot.hidden = true;
      } else {
        /* Drawer: start closed, move sort inside so the pill earns its name. */
        if (this.drawer) this.drawer.open = false;
        if (this.sortSlot && this.sortWrap) {
          this.sortSlot.hidden = false;
          if (this.sortWrap.parentNode !== this.sortSlot) {
            this.sortSlot.appendChild(this.sortWrap);
          }
        }
      }
    }
  };

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    context.querySelectorAll('[data-dw-facets]').forEach(function (el) {
      if (el.__dwFacets) return;
      el.__dwFacets = new DwFacets(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* Theme Editor: sections re-render on setting changes. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
